// Colour ramps for the reach map (height above ground) and region search (height margin).

export type RGB = [number, number, number];

export interface Stop {
  v: number;
  c: RGB;
}

/** Height above ground, metres. */
export const AGL_STOPS: Stop[] = [
  { v: 0, c: [215, 25, 28] },
  { v: 150, c: [253, 174, 97] },
  { v: 400, c: [255, 255, 140] },
  { v: 800, c: [166, 217, 106] },
  { v: 1500, c: [26, 150, 65] },
  { v: 2500, c: [43, 131, 186] },
];

/** Launch height margin, metres. */
export const MARGIN_STOPS: Stop[] = [
  { v: 0, c: [255, 237, 160] },
  { v: 100, c: [254, 178, 76] },
  { v: 300, c: [240, 59, 32] },
  { v: 600, c: [189, 0, 38] },
];

export function ramp(stops: Stop[], v: number): RGB {
  if (v <= stops[0].v) return stops[0].c;
  for (let i = 1; i < stops.length; i++) {
    if (v <= stops[i].v) {
      const a = stops[i - 1];
      const b = stops[i];
      const t = (v - a.v) / (b.v - a.v);
      return [0, 1, 2].map((k) => Math.round(a.c[k] + (b.c[k] - a.c[k]) * t)) as RGB;
    }
  }
  return stops[stops.length - 1].c;
}

export const css = (c: RGB) => `rgb(${c[0]},${c[1]},${c[2]})`;

/** Render a value grid to RGBA pixels (NaN -> transparent). */
export function gridToRgba(values: ArrayLike<number>, stops: Stop[], alpha = 170): Uint8ClampedArray {
  const out = new Uint8ClampedArray(values.length * 4);
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (Number.isNaN(v)) continue;
    const c = ramp(stops, v);
    out[i * 4] = c[0];
    out[i * 4 + 1] = c[1];
    out[i * 4 + 2] = c[2];
    out[i * 4 + 3] = alpha;
  }
  return out;
}

const BAND_COLORS = ['#e4572e', '#f39237', '#f6cf4a', '#b5d86a', '#5cb96b', '#21917f'];

function hexRgb(h: string): RGB {
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as RGB;
}

/** Colour at position t (0..1) along the band palette. */
function bandColor(t: number): string {
  const x = Math.min(1, Math.max(0, t)) * (BAND_COLORS.length - 1);
  const i = Math.min(BAND_COLORS.length - 2, Math.floor(x));
  const a = hexRgb(BAND_COLORS[i]);
  const b = hexRgb(BAND_COLORS[i + 1]);
  const f = x - i;
  return css([0, 1, 2].map((k) => Math.round(a[k] + (b[k] - a[k]) * f)) as RGB);
}

export interface Bands {
  /** Lower bound of each band, metres. */
  thresholds: number[];
  /** Lower bound of each band in display units. */
  labels: number[];
  colors: string[];
}

/**
 * Height-above-ground bands fitted to this result: about five bands with round steps in the
 * display unit, so a small hill shows 0/100/200/300 ft and the Alps 0/500/1000… m. Warm is the
 * lowest band (least height to spare), cool the highest.
 */
export function aglBands(alt: 'm' | 'ft', maxAglM: number): Bands {
  const toDisp = (m: number) => (alt === 'ft' ? m / 0.3048 : m);
  const steps = alt === 'ft' ? [50, 100, 200, 250, 500, 1000, 2000] : [20, 25, 50, 100, 200, 250, 500, 1000];
  const max = Math.max(toDisp(maxAglM), steps[0] * 2);
  // the round step that gives closest to five bands
  const step = steps.reduce((best, s) => (Math.abs(Math.ceil(max / s) - 5) < Math.abs(Math.ceil(max / best) - 5) ? s : best));
  const labels: number[] = [];
  for (let v = 0; v < max; v += step) labels.push(v);
  if (labels.length < 2) labels.push(step);
  const thresholds = labels.map((v) => (alt === 'ft' ? v * 0.3048 : v));
  const n = labels.length;
  const colors = labels.map((_, i) => bandColor(n === 1 ? 0 : i / (n - 1)));
  return { thresholds, labels, colors };
}

/** Bands for the region-search margin (height to spare on arrival), metres. */
export const MARGIN_BANDS = { thresholds: [0, 100, 300, 600], colors: ['#fde68a', '#fbbf24', '#f97316', '#c2410c'] };
